import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
// DashboardSidebar and DashboardHeader are now in DashboardLayout
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Sparkles, Loader2, X, Search, Lightbulb, PenTool, Check } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { useLanguage } from '@/contexts/LanguageContext';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { ContentCard } from '@/components/ContentCard';
import { ContentIdea } from '@/types/dashboard';
import { Progress } from '@/components/ui/progress';
import { useUserId } from '@/hooks/useUserId';
import { AnimatedPlaceholder } from '@/components/ui/animated-placeholder';
import { GenerationUsageCounter, useGenerationCounts } from '@/components/GenerationUsageCounter';
import { logError, trackGeneration } from '@/utils/sentry';

interface GeneratedContent {
  id: string;
  title: string;
  description: string | null;
  content: string | null;
  alt_hooks: unknown;
  text_overlays: unknown;
  improvements_applied: unknown;
  perplexity_insights: unknown;
  platform: string | null;
  hashtags: string[] | null;
  viral_score: number | null;
  is_favorited?: boolean;
  is_used?: boolean;
  main_idea?: string;
}

const PLATFORMS = ['instagram', 'tiktok', 'facebook', 'youtube'] as const;
type Platform = typeof PLATFORMS[number];

const PROGRESS_INTERVAL_MS = 1000;
const POLL_INTERVAL_MS = 3000;
const PROGRESS_MAX_BEFORE_COMPLETE = 97;

// Timing configs per video length
const GENERATION_TIMING = {
  short: {
    estimatedMs: 3 * 60 * 1000,       // ~3 minutes
    errorTimeoutMs: 5 * 60 * 1000,    // 5 minutes error timeout
    hardTimeoutMs: 10 * 60 * 1000,    // 10 minutes max
    label: '~3 minuty',
  },
  long: {
    estimatedMs: 5 * 60 * 1000,       // ~5 minutes
    errorTimeoutMs: 7 * 60 * 1000,    // 7 minutes error timeout
    hardTimeoutMs: 12 * 60 * 1000,    // 12 minutes max
    label: '~5 minut',
  },
} as const;

// Eased progress: starts fast, decelerates over time (feels snappy)
// Uses an ease-out curve: progress = max * (1 - (1 - t)^2.5)
const getEasedProgress = (elapsedMs: number, estimatedMs: number): number => {
  const t = Math.min(elapsedMs / estimatedMs, 1);
  return PROGRESS_MAX_BEFORE_COMPLETE * (1 - Math.pow(1 - t, 2.5));
};

// Normalize JSONB fields (string | object | array) to string[] for UI components
const normalizeArrayField = (value: unknown): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) return value.map((v) => String(v));
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      try { return normalizeArrayField(JSON.parse(trimmed)); } catch { return [value]; }
    }
    return [value];
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const keys = ['hooks','alt_hooks','overlays','text_overlays','items','list','data','values'];
    for (const k of keys) {
      if (Array.isArray(obj[k])) return (obj[k] as unknown[]).map((v) => String(v));
    }
    const vals = Object.values(obj);
    if (vals.every((v) => typeof v === 'string')) return vals as string[];
  }
  return [];
};

// Extract script from content field (which may be JSON string)
const extractScript = (content: string | null): string => {
  if (!content) return '';
  try {
    const parsed = JSON.parse(content);
    return parsed.script || content;
  } catch {
    return content;
  }
};

// Animated placeholder texts for typing effect
const ANIMATED_PLACEHOLDERS = [
  'Jak oszczędzać czas przy tworzeniu treści do mediów społecznościowych',
  'Dlaczego większość ludzi nigdy nie zacznie prowadzić social mediów',
  'Najczęstsze błędy przy nagrywaniu pierwszych Reelsów',
  'Jak zdobyć pierwsze 1000 obserwujących bez płatnych reklam',
  'Co zrobić, gdy nikt nie ogląda Twoich treści',
];

// Map category prefix to example topic (legacy - used for fallback)
const getCategoryPlaceholder = (category: string | null): string => {
  if (!category) return 'np. Najlepsze polskie wynalazki, które zmieniły świat';
  
  const prefix = category.split('_')[0];
  
  const placeholders: Record<string, string> = {
    creator: 'np. Dlaczego Twoje treści nie dają żadnych efektów',
    local: 'np. Jak nie dać się naciągnąć przy wyborze usługi',
    brand: 'np. Jak uniknąć złego zakupu online',
    agency: 'np. Jak nie przepalić budżetu na współpracę z agencją',
    startup: 'np. Dlaczego narzędzia, które kupujesz, nie rozwiązują problemu',
    real: 'np. Na co uważać, żeby nie stracić pieniędzy na nieruchomości',
    gastro: 'np. Dlaczego restauracja rozczarowuje mimo dobrych opinii',
    edu: 'np. Dlaczego kursy i szkolenia często nie działają',
    art: 'np. Dlaczego większość treści jest nudna po 10 sekundach',
  };
  
  return placeholders[prefix] || 'np. Najlepsze polskie wynalazki, które zmieniły świat';
};

interface TopicSuggestion {
  id: number;
  text: string;
}

const TONE_OPTIONS = [
  { label: 'Neutralny', value: 'neutralny', icon: '📋' },
  { label: 'Profesjonalny', value: 'profesjonalny', icon: '💼' },
  { label: 'Swobodny', value: 'swobodny', icon: '😊' },
  { label: 'Humorystyczny', value: 'humorystyczny', icon: '😄' },
];

const GENDER_OPTIONS = [
  { label: 'Wszyscy', value: 'neutralny', icon: '👥' },
  { label: 'Kobiety', value: 'kobieta', icon: '👩' },
  { label: 'Mężczyźni', value: 'mężczyzna', icon: '👨' },
];

const VIDEO_LENGTH_OPTIONS = [
  { label: 'Krótki format', value: 'krotki', icon: '⚡', subtitle: '~30 sekund' },
  { label: 'Dłuższe wideo', value: 'dluzszy', icon: '🎬', subtitle: '60+ sekund' },
];

const MODE_OPTIONS = [
{ 
    id: 'szybki' as const, 
    label: 'Podstawowy', 
    emoji: '⚡',
    description: 'Szybki start'
  },
  { 
    id: 'dostosowany' as const, 
    label: 'Dopasowany', 
    emoji: '🎨',
    description: 'Ton i styl pod siebie',
    isDefault: true
  },
];

const LOADING_TIPS = [
  { emoji: '🔍', text: 'Szukam ciekawych tematów...' },
  { emoji: '💡', text: 'Analizuję Twój pomysł...' },
  { emoji: '🎯', text: 'Dobieram najlepsze ujęcie tematu...' },
  { emoji: '✨', text: 'Przygotowuję scenariusz...' },
  { emoji: '🚀', text: 'Dopracowuję szczegóły...' },
];

const GENERATION_STEPS = [
  { emoji: '🔎', label: 'Analiza tematu', text: 'Sprawdzam Twój pomysł' },
  { emoji: '🧠', label: 'Tworzenie scenariusza', text: 'Buduję strukturę' },
  { emoji: '✍️', label: 'Pisanie treści', text: 'Dopracowuję hook i CTA' },
  { emoji: '✨', label: 'Finalizacja', text: 'Ostatnie szlify' },
];

const LoadingSuggestionsState = () => {
  const [tipIndex, setTipIndex] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setTipIndex((prev) => (prev + 1) % LOADING_TIPS.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const currentTip = LOADING_TIPS[tipIndex];

  return (
    <div className="py-8 space-y-6">
      {/* Animated loader */}
      <div className="flex justify-center">
        <div className="relative">
          <div className="w-16 h-16 rounded-full border-4 border-primary/20 animate-pulse" />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-2xl animate-bounce">{currentTip.emoji}</span>
          </div>
        </div>
      </div>
      
      {/* Rotating tip text */}
      <div className="text-center space-y-2">
        <p 
          key={tipIndex}
          className="text-sm font-medium text-foreground animate-in fade-in-0 duration-300"
        >
          {currentTip.text}
        </p>
        <p className="text-xs text-muted-foreground">
          To potrwa tylko chwilę
        </p>
      </div>
      
      {/* Progress dots */}
      <div className="flex justify-center gap-1.5">
        {LOADING_TIPS.map((_, i) => (
          <div
            key={i}
            className={cn(
              "w-2 h-2 rounded-full transition-all duration-300",
              i === tipIndex ? "bg-primary w-4" : "bg-muted-foreground/30"
            )}
          />
        ))}
      </div>
    </div>
  );
};

interface GeneratingScenarioStateProps {
  progress: number;
  timeLabel?: string;
}

const GeneratingScenarioState = ({ progress, timeLabel = '~3 minuty' }: GeneratingScenarioStateProps) => {
  // Determine step based on progress
  const getStepIndex = () => {
    if (progress <= 25) return 0;
    if (progress <= 50) return 1;
    if (progress <= 75) return 2;
    return 3;
  };

  const stepIndex = getStepIndex();
  const currentStep = GENERATION_STEPS[stepIndex];

  return (
    <div className="mt-8 pt-8 border-t border-border/30 space-y-6 animate-in fade-in-0 slide-in-from-bottom-2 duration-300">
      {/* Header */}
      <div className="flex items-center justify-center gap-3 text-primary">
        <Sparkles className="w-6 h-6 animate-pulse" />
        <span className="text-lg font-semibold">Tworzymy dla Ciebie scenariusz</span>
      </div>

      {/* Progress bar with percentage */}
      <div className="space-y-4 bg-muted/30 rounded-xl p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{currentStep.emoji}</span>
            <div>
              <p className="text-base font-semibold text-foreground">{currentStep.label}</p>
              <p className="text-sm text-muted-foreground">{currentStep.text}</p>
            </div>
          </div>
          <span className="text-lg font-bold text-primary">{Math.round(progress)}%</span>
        </div>

        <Progress value={progress} className="h-3" />
      </div>

      {/* Time estimate */}
      <p className="text-sm text-muted-foreground text-center">
        Średni czas tworzenia scenariusza: {timeLabel}
      </p>
    </div>
  );
};

const IdeaGenerator = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { toast } = useToast();
  const userId = useUserId();
  const generationCounts = useGenerationCounts();
  
  // Memoized localStorage keys - stable once userId is available
  const STORAGE_KEY = useMemo(() => userId ? `idea_generator_latest_content_${userId}` : null, [userId]);
  const GENERATING_STATE_KEY = useMemo(() => userId ? `idea_generator_active_generation_${userId}` : null, [userId]);
  
  const [mainIdea, setMainIdea] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState<Platform>('instagram');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState<GeneratedContent | null>(null);
  const [progress, setProgress] = useState(0);
  const [userCategory, setUserCategory] = useState<string | null>(null);
  
  // Track active intervals/timeouts for cleanup - use refs to persist across renders
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const errorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasRestoredStateRef = useRef(false);
  
  // Shared flow state: input → suggestions → mode_selection
  const [flowStep, setFlowStep] = useState<'input' | 'suggestions' | 'mode_selection'>('input');
  const [suggestions, setSuggestions] = useState<TopicSuggestion[]>([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState<string | null>(null);
  const [customTopic, setCustomTopic] = useState('');
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  
  // Mode selection (only visible after topic selected)
  const [generationMode, setGenerationMode] = useState<'szybki' | 'dostosowany' | 'rozszerzony' | null>(null);
  
  // Dostosowany mode controls
  const [toneIndex, setToneIndex] = useState(0);
  const [genderIndex, setGenderIndex] = useState(0); // "Wszyscy" is now index 0
  const [videoLength, setVideoLength] = useState<string | null>(null);
  const [showVideoLengthHint, setShowVideoLengthHint] = useState(false);
  const [generationTimeLabel, setGenerationTimeLabel] = useState<string>(GENERATION_TIMING.short.label);

  // Cleanup intervals on unmount - but DON'T clear localStorage
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
      // Reset restoration flag on unmount so it can restore again on remount
      hasRestoredStateRef.current = false;
    };
  }, []);

  // Clear old non-user-specific localStorage data on mount
  useEffect(() => {
    if (userId) {
      localStorage.removeItem('idea_generator_latest_content');
      localStorage.removeItem('idea_generator_active_generation');
    }
  }, [userId]);

  // Fetch user's category for dynamic placeholder
  useEffect(() => {
    const fetchUserCategory = async () => {
      if (!userId) return;
      
      const { data } = await supabase
        .from('user_profiles')
        .select('category')
        .eq('user_id', userId)
        .maybeSingle();
      
      if (data?.category) {
        setUserCategory(data.category);
      }
    };
    
    fetchUserCategory();
  }, [userId]);

  // Persist generation state across page refresh using multiple storage backends.
  // Some browser extensions (e.g. MetaMask/SES) clear localStorage and sessionStorage
  // on page reload, so we also use window.name which survives same-tab refreshes.
  const persistState = useCallback((key: string, value: string | null) => {
    if (value) {
      try { localStorage.setItem(key, value); } catch { /* ignore */ }
      try { sessionStorage.setItem(key, value); } catch { /* ignore */ }
      try {
        let wn: Record<string, string> = {};
        try { wn = JSON.parse(window.name) || {}; } catch { wn = {}; }
        wn[key] = value;
        window.name = JSON.stringify(wn);
      } catch { /* ignore */ }
    } else {
      try { localStorage.removeItem(key); } catch { /* ignore */ }
      try { sessionStorage.removeItem(key); } catch { /* ignore */ }
      try {
        let wn: Record<string, string> = {};
        try { wn = JSON.parse(window.name) || {}; } catch { wn = {}; }
        delete wn[key];
        window.name = JSON.stringify(wn);
      } catch { /* ignore */ }
    }
  }, []);

  const readPersistedState = useCallback((key: string): string | null => {
    try { const v = localStorage.getItem(key); if (v) return v; } catch { /* ignore */ }
    try { const v = sessionStorage.getItem(key); if (v) return v; } catch { /* ignore */ }
    try {
      const wn = JSON.parse(window.name);
      if (wn && wn[key]) return wn[key];
    } catch { /* ignore */ }
    return null;
  }, []);

  // Load persisted content and check for active generation on mount AND on every navigation back
  useEffect(() => {
    // Skip if already restoring or if userId/keys not ready
    if (!userId || !STORAGE_KEY || !GENERATING_STATE_KEY) {
      return;
    }

    // Prevent duplicate restoration in the same mount cycle
    if (hasRestoredStateRef.current) {
      return;
    }
    hasRestoredStateRef.current = true;

    // Clear any existing intervals first to prevent duplicates
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }
    
    try {
      // Load saved content
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setGeneratedContent(parsed);
      }

      // Check if there's an active generation (check all storage backends)
      const generatingState = readPersistedState(GENERATING_STATE_KEY);

      if (generatingState) {
        const parsedState = JSON.parse(generatingState);
        const { isGenerating: wasGenerating, startTime, mainIdea: savedIdea, platform, videoLength: savedVideoLength, generationMode: savedMode, toneIndex: savedTone, genderIndex: savedGender } = parsedState;

        if (wasGenerating && startTime) {
          const isLong = savedVideoLength === 'dluzszy';
          const restoredTiming = isLong ? GENERATION_TIMING.long : GENERATION_TIMING.short;
          const elapsed = Date.now() - startTime;
          const maxDuration = restoredTiming.hardTimeoutMs;

          if (elapsed < maxDuration) {

            // Resume generation state
            setIsGenerating(true);
            setMainIdea(savedIdea || '');
            setSelectedPlatform(platform || 'instagram');
            setSelectedSuggestion(savedIdea || null);
            setFlowStep('mode_selection');
            setGenerationTimeLabel(restoredTiming.label);
            // Restore generation settings so the UI shows what was selected
            if (savedMode) setGenerationMode(savedMode);
            if (savedVideoLength) setVideoLength(savedVideoLength);
            if (typeof savedTone === 'number') setToneIndex(savedTone);
            if (typeof savedGender === 'number') setGenderIndex(savedGender);

            // Calculate eased progress based on elapsed time
            const estimatedProgress = getEasedProgress(elapsed, restoredTiming.estimatedMs);
            setProgress(Math.min(estimatedProgress, PROGRESS_MAX_BEFORE_COMPLETE));

            // Continue eased progress updates
            progressIntervalRef.current = setInterval(() => {
              const currentElapsed = Date.now() - startTime;
              const easedValue = getEasedProgress(currentElapsed, restoredTiming.estimatedMs);
              setProgress(Math.min(easedValue, PROGRESS_MAX_BEFORE_COMPLETE));
              if (easedValue >= PROGRESS_MAX_BEFORE_COMPLETE && progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
                progressIntervalRef.current = null;
              }
            }, PROGRESS_INTERVAL_MS);

            // Poll for completion
            const checkCompletion = async () => {
              try {
                const { data: { user } } = await supabase.auth.getUser();
                if (!user) return;

                const { data, error } = await supabase
                  .from('user_generated_content' as any)
                  .select('*')
                  .eq('user_id', user.id)
                  .gte('created_at', new Date(startTime).toISOString())
                  .order('created_at', { ascending: false })
                  .limit(1)
                  .maybeSingle();

                if (!error && data) {
                  
                  if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
                  if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
                  if (timeoutRef.current) clearTimeout(timeoutRef.current);
                  progressIntervalRef.current = null;
                  pollIntervalRef.current = null;
                  timeoutRef.current = null;
                  
                  setGeneratedContent(data as unknown as GeneratedContent);
                  setProgress(100);
                  setIsGenerating(false);
                  persistState(GENERATING_STATE_KEY, null);
                  setTimeout(() => setProgress(0), 1000);
                  
                  generationCounts.refresh();
                  toast({
                    title: "Gotowe",
                    description: "Twój scenariusz jest gotowy do użycia.",
                  });
                }
              } catch (error) {
                console.error('[IdeaGenerator] Error checking completion:', error);
              }
            };

            pollIntervalRef.current = setInterval(checkCompletion, POLL_INTERVAL_MS);

            // Also check immediately on mount
            checkCompletion();

            // Set timeout for max duration
            const remainingTime = maxDuration - elapsed;
            timeoutRef.current = setTimeout(() => {
              if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
              if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
              progressIntervalRef.current = null;
              pollIntervalRef.current = null;
              timeoutRef.current = null;
              
              setIsGenerating(false);
              setProgress(0);
              persistState(GENERATING_STATE_KEY, null);
            }, remainingTime);
          } else {
            // Generation took too long, clear state
            persistState(GENERATING_STATE_KEY, null);
            setIsGenerating(false);
            setProgress(0);
          }
        }
      }
    } catch (e) {
      console.error('[IdeaGenerator] Failed to load saved state:', e);
      // Don't remove GENERATING_STATE_KEY here — a transient error (e.g. auth not ready)
      // shouldn't wipe the generation state. It will be cleaned up on timeout or success.
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, STORAGE_KEY, GENERATING_STATE_KEY, toast]);

  // Save content to localStorage whenever it changes
  useEffect(() => {
    if (generatedContent && userId && STORAGE_KEY) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(generatedContent));
      } catch (e) {
        console.error('Failed to save content:', e);
      }
    }
  }, [generatedContent, userId, STORAGE_KEY]);

  const clearGeneratedContent = () => {
    setGeneratedContent(null);
    if (STORAGE_KEY) {
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  const saveGeneratingState = useCallback((generating: boolean, idea?: string, platform?: Platform, vidLength?: string, mode?: string, tone?: number, gender?: number) => {
    if (!GENERATING_STATE_KEY) {
      console.warn('[IdeaGenerator] Cannot save generating state - key not available');
      return;
    }

    if (generating) {
      const state = {
        isGenerating: true,
        startTime: Date.now() - 30000,
        mainIdea: idea,
        platform,
        videoLength: vidLength,
        generationMode: mode,
        toneIndex: tone,
        genderIndex: gender,
      };
      persistState(GENERATING_STATE_KEY, JSON.stringify(state));
    } else {
      persistState(GENERATING_STATE_KEY, null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [GENERATING_STATE_KEY]);

  // Handle fetching topic suggestions (shared across all modes)
  const handleGenerateSuggestions = async () => {
    if (!mainIdea || mainIdea.trim().length < 10) {
      toast({
        title: "Błąd walidacji",
        description: "Opis musi mieć minimum 10 znaków",
        variant: "destructive"
      });
      return;
    }

    setIsLoadingSuggestions(true);
    setFlowStep('suggestions');
    setSuggestions([]);
    setSelectedSuggestion(null);
    setCustomTopic('');
    setGenerationMode(null);

    try {
      // Call Edge Function instead of direct n8n webhook
      const { data, error } = await supabase.functions.invoke('topic-suggestions', {
        body: { main_topic: mainIdea.trim() }
      });

      if (error) {
        throw new Error(error.message || 'Failed to fetch suggestions');
      }
      
      if (data?.suggestions && Array.isArray(data.suggestions)) {
        setSuggestions(data.suggestions);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      logError('IdeaGenerator: fetch suggestions failed', error, { topic: mainIdea.trim().slice(0, 50) });
      toast({
        title: "Błąd",
        description: "Nie udało się wygenerować podpowiedzi. Spróbuj ponownie.",
        variant: "destructive"
      });
      setFlowStep('input');
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  // Handle suggestion selection → show mode buttons
  const handleSelectSuggestion = (suggestionText: string) => {
    setSelectedSuggestion(suggestionText);
    setCustomTopic('');
    setFlowStep('mode_selection');
  };

  // Handle custom topic submission → show mode buttons
  const handleCustomTopicSubmit = () => {
    if (customTopic.trim().length >= 10) {
      setSelectedSuggestion(null);
      setFlowStep('mode_selection');
    } else {
      toast({
        title: "Błąd walidacji",
        description: "Własny temat musi mieć minimum 10 znaków",
        variant: "destructive"
      });
    }
  };

  // Get the selected topic (from suggestion or custom input)
  const getSelectedTopic = () => {
    return selectedSuggestion || customTopic.trim();
  };

  const handleGenerate = async () => {
    // Validate mode selection
    if (!generationMode) {
      toast({
        title: "Błąd walidacji",
        description: "Wybierz tryb generowania",
        variant: "destructive"
      });
      return;
    }

    // Validate video length in dostosowany mode
    if (generationMode === 'dostosowany' && !videoLength) {
      setShowVideoLengthHint(true);
      return;
    }

    // Always use the selected topic from flow
    const topicToUse = getSelectedTopic();

    // Validation
    if (!topicToUse || topicToUse.length < 10) {
      toast({
        title: "Błąd walidacji",
        description: "Opis musi mieć minimum 10 znaków",
        variant: "destructive"
      });
      return;
    }
    
    if (topicToUse.length > 1000) {
      toast({
        title: "Błąd walidacji",
        description: "Opis może mieć maksymalnie 1000 znaków",
        variant: "destructive"
      });
      return;
    }

    setIsGenerating(true);
    setProgress(0);
    trackGeneration('started', { mode: generationMode, platform: selectedPlatform });

    // Subtract 30s buffer to account for clock skew between browser and database server
    const generationStartTime = Date.now() - 30000;

    // Determine timing based on video length (long videos take up to 5 min)
    const isLongVideo = videoLength === 'dluzszy';
    const timing = isLongVideo ? GENERATION_TIMING.long : GENERATION_TIMING.short;
    setGenerationTimeLabel(timing.label);

    // Save generating state (including all settings for restore)
    saveGeneratingState(true, topicToUse, selectedPlatform, isLongVideo ? 'dluzszy' : 'krotki', generationMode || undefined, toneIndex, genderIndex);

    // Clear previous content when starting new generation
    clearGeneratedContent();

    // Clear any existing timers to prevent duplicates
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }

    // Eased progress simulation: fast start, decelerates over time
    progressIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - generationStartTime;
      const easedValue = getEasedProgress(elapsed, timing.estimatedMs);
      setProgress(Math.min(easedValue, PROGRESS_MAX_BEFORE_COMPLETE));
      if (easedValue >= PROGRESS_MAX_BEFORE_COMPLETE && progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    }, PROGRESS_INTERVAL_MS);

    try {
      // Get current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        toast({
          title: "Błąd autoryzacji",
          description: "Musisz być zalogowany",
          variant: "destructive"
        });
        return;
      }

      // Start polling for completion regardless of webhook response
      const pollForCompletion = async () => {
        try {
          const { data, error } = await supabase
            .from('user_generated_content' as any)
            .select('*')
            .eq('user_id', user.id)
            .gte('created_at', new Date(generationStartTime).toISOString())
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (!error && data) {
            if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
            progressIntervalRef.current = null;
            pollIntervalRef.current = null;
            timeoutRef.current = null;
            errorTimeoutRef.current = null;

            setGeneratedContent(data as unknown as GeneratedContent);
            setProgress(100);
            setIsGenerating(false);
            saveGeneratingState(false);
            setTimeout(() => setProgress(0), 1000);

            trackGeneration('completed', { durationMs: Date.now() - generationStartTime });
            generationCounts.refresh();
            toast({
              title: "Gotowe",
              description: "Twój scenariusz jest gotowy do użycia.",
            });

            // Keep settings visible so user can tweak and regenerate
            setShowVideoLengthHint(false);
            return true;
          }
          return false;
        } catch (error) {
          console.error('Error polling for completion:', error);
          return false;
        }
      };

      // Poll every 3 seconds
      pollIntervalRef.current = setInterval(pollForCompletion, POLL_INTERVAL_MS);

      // Error timeout - show error message (dynamic based on video length)
      errorTimeoutRef.current = setTimeout(() => {
        if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
        if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        progressIntervalRef.current = null;
        pollIntervalRef.current = null;
        timeoutRef.current = null;
        errorTimeoutRef.current = null;

        setIsGenerating(false);
        setProgress(0);
        saveGeneratingState(false);

        trackGeneration('timeout', { timeoutMs: timing.errorTimeoutMs });
        toast({
          title: "Błąd generowania",
          description: "Nie udało się wygenerować scenariusza. Spróbuj ponownie.",
          variant: "destructive"
        });
      }, timing.errorTimeoutMs);

      timeoutRef.current = setTimeout(() => {
        if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
        if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
        progressIntervalRef.current = null;
        pollIntervalRef.current = null;
        timeoutRef.current = null;
        errorTimeoutRef.current = null;

        setIsGenerating(false);
        setProgress(0);
        saveGeneratingState(false);
      }, timing.hardTimeoutMs);

      // Call Edge Function instead of direct n8n webhook
      const requestBody: Record<string, unknown> = {
        user_id: user.id,
        main_idea: topicToUse,
        platform: selectedPlatform,
        mode: generationMode
      };

      // Add tone, gender, and video_length for dostosowany mode
      if (generationMode === 'dostosowany') {
        requestBody.tone = TONE_OPTIONS[toneIndex].value;
        requestBody.gender = GENDER_OPTIONS[genderIndex].value;
        requestBody.video_length = videoLength;
      }

      try {
        const { data, error } = await supabase.functions.invoke('generate-content', {
          body: requestBody
        });

        if (error) {
          console.warn('Edge Function returned error:', error);
          
          // Stop all generation state for ANY error
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
          }
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          if (errorTimeoutRef.current) {
            clearTimeout(errorTimeoutRef.current);
            errorTimeoutRef.current = null;
          }
          setIsGenerating(false);
          setProgress(0);
          saveGeneratingState(false);
          
          // Check if it's a rate limit error (429)
          const isRateLimit = error.context?.status === 429 || 
                             error.message?.includes('limit') || 
                             error.message?.includes('Limit') ||
                             error.message?.includes('429');
          
          if (isRateLimit) {
            toast({
              title: "Limit wyczerpany",
              description: "Wykorzystałeś limit generacji na ten miesiąc. Zmień plan, aby kontynuować.",
              variant: "destructive"
            });
          } else {
            toast({
              title: "Błąd generowania",
              description: error.message || "Wystąpił błąd. Spróbuj ponownie.",
              variant: "destructive"
            });
          }
          return; // Exit early - don't continue with polling
        }
        
        if (data) {
          console.log('Generation started, remaining:', data.remaining);
        }
      } catch (error) {
        console.warn('Edge Function call failed:', error);
        // Stop generation on any exception too
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        if (errorTimeoutRef.current) {
          clearTimeout(errorTimeoutRef.current);
          errorTimeoutRef.current = null;
        }
        setIsGenerating(false);
        setProgress(0);
        saveGeneratingState(false);
        return;
      }    } catch (error: any) {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
        errorTimeoutRef.current = null;
      }

      setProgress(0);
      saveGeneratingState(false);
      setIsGenerating(false);
      logError('IdeaGenerator: generation failed', error, { flow: 'generation' });
      trackGeneration('error', { errorMessage: error?.message });

      toast({
        title: "Błąd generowania",
        description: "Wystąpił błąd. Spróbuj ponownie.",
        variant: "destructive"
      });
    }
  };

  const isFormValid = mainIdea.trim().length >= 10 && mainIdea.length <= 1000;

  if (generationCounts.loading) {
    return (
      <main className="flex-1 overflow-y-auto p-3 md:p-6 flex justify-center">
        <div className="w-full max-w-[1200px] flex items-center justify-center mt-20">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </main>
    );
  }

  return (
    <main className="flex-1 overflow-y-auto p-3 md:p-6 flex justify-center">
      <div className="w-full max-w-[1200px] space-y-3 md:space-y-6 mt-2 md:mt-4">
        <Card className="p-4 md:p-8 backdrop-blur-xl bg-card/80 border-border/50 shadow-xl mb-32 md:mb-40">
              <div className="space-y-6">

                {/* Usage counter - top right (Pro: only shown when ≤5 remaining) */}
                <div className="flex flex-col items-end gap-1 -mb-2">
                  <GenerationUsageCounter data={generationCounts} />
                  {generationCounts.resolvedTier === 'trial' && (
                    <p className="text-xs text-muted-foreground">
                      Po aktywacji: 15 podstawowych + 5 wzbogaconych miesięcznie (Starter) lub 60 + 30 (Pro).
                    </p>
                  )}
                </div>

                {/* Section 1: Topic Input - Always visible */}
                <div className="space-y-4">
                  <h1 className="text-xl md:text-2xl font-semibold text-foreground text-center">
                    O czym chcesz dzisiaj nagrać?
                  </h1>
                  
                  <div className="space-y-2">
                    <div className="relative">
                      <Textarea
                        id="topic"
                        placeholder=""
                        value={mainIdea}
                        onChange={(e) => {
                          setMainIdea(e.target.value);
                          // Auto-resize textarea
                          e.target.style.height = 'auto';
                          e.target.style.height = `${Math.max(100, e.target.scrollHeight)}px`;
                          // Reset downstream selections when topic changes
                          if (suggestions.length > 0) {
                            setSuggestions([]);
                            setSelectedSuggestion(null);
                            setFlowStep('input');
                          }
                        }}
                        className={cn(
                          "min-h-[100px] py-4 px-4 resize-none text-base bg-background border-2 rounded-xl focus:ring-2 transition-all shadow-sm overflow-hidden",
                          mainIdea.length > 0 && mainIdea.trim().length < 10
                            ? "border-destructive/50 focus:border-destructive focus:ring-destructive/20"
                            : "border-primary/30 focus:border-primary focus:ring-primary/20"
                        )}
                        disabled={isGenerating}
                        maxLength={300}
                        rows={3}
                      />
                      {/* Animated placeholder overlay */}
                      {!mainIdea && !isGenerating && (
                        <div className="absolute top-4 left-4 right-4 pointer-events-none">
                          <AnimatedPlaceholder 
                            placeholders={ANIMATED_PLACEHOLDERS}
                            typingSpeed={40}
                            deletingSpeed={20}
                            pauseDuration={2500}
                            className="text-base"
                          />
                        </div>
                      )}
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      {mainIdea.length > 0 && mainIdea.trim().length < 10 ? (
                        <p className="text-xs text-destructive">
                          Minimum 10 znaków — opisz temat dokładniej
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          Wpisz ogólny temat - pomożemy go doprecyzować
                        </p>
                      )}
                      <p className={cn(
                        "text-xs whitespace-nowrap",
                        mainIdea.length > 270 ? "text-warning font-semibold" : "text-muted-foreground"
                      )}>
                        {mainIdea.length}/300
                      </p>
                    </div>
                  </div>

                  {/* Show suggestions button only when no suggestions loaded */}
                  {flowStep === 'input' && (
                    <Button
                      onClick={handleGenerateSuggestions}
                      disabled={!isFormValid || isLoadingSuggestions}
                      className="w-full h-12 text-base font-medium"
                      size="lg"
                    >
                      {isLoadingSuggestions ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Szukam pomysłów...
                        </>
                      ) : (
                        <>
                          <Lightbulb className="mr-2 h-5 w-5" />
                          Pokaż pomysły
                        </>
                      )}
                    </Button>
                  )}
                </div>

                {/* Section 2: Topic Suggestions - Shows after clicking "Pokaż pomysły" */}
                {(flowStep === 'suggestions' || flowStep === 'mode_selection') && (
                  <div className="space-y-3 pt-2 border-t border-border/30 animate-in fade-in-0 duration-300">
                    <div className="flex items-center justify-between">
                      <h2 className="text-base font-medium text-foreground">
                        Wybierz wersję
                      </h2>
                      {selectedSuggestion && !isGenerating && (
                        <button
                          onClick={() => {
                            setSelectedSuggestion(null);
                            setFlowStep('suggestions');
                            setGenerationMode(null);
                          }}
                          className="text-xs text-primary hover:underline"
                        >
                          Zmień wybór
                        </button>
                      )}
                    </div>

                    {isLoadingSuggestions ? (
                      <LoadingSuggestionsState />
                    ) : isGenerating && selectedSuggestion ? (
                      /* Collapsed view - show only selected suggestion during generation */
                      <div className="p-3 rounded-lg border border-primary bg-primary/10">
                        <div className="flex items-start gap-3 text-sm">
                          <div className="w-5 h-5 rounded-full border-2 border-primary bg-primary flex-shrink-0 flex items-center justify-center mt-0.5">
                            <Check className="w-3 h-3 text-primary-foreground" />
                          </div>
                          <span className="leading-relaxed font-medium">{selectedSuggestion}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {suggestions.map((suggestion, index) => (
                          <button
                            key={suggestion.id}
                            type="button"
                            onClick={() => handleSelectSuggestion(suggestion.text)}
                            className={cn(
                              "w-full p-3 text-left rounded-lg border transition-all duration-200",
                              "flex items-start gap-3 group text-sm",
                              selectedSuggestion === suggestion.text
                                ? "border-primary bg-primary/10"
                                : "border-border/50 bg-background/30 hover:border-primary/50 hover:bg-primary/5"
                            )}
                          >
                            <div className={cn(
                              "w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center mt-0.5 transition-colors",
                              selectedSuggestion === suggestion.text
                                ? "border-primary bg-primary"
                                : "border-muted-foreground/30 group-hover:border-primary"
                            )}>
                              {selectedSuggestion === suggestion.text ? (
                                <Check className="w-3 h-3 text-primary-foreground" />
                              ) : (
                                <span className="text-xs text-muted-foreground group-hover:text-primary">{index + 1}</span>
                              )}
                            </div>
                            <span className="leading-relaxed">{suggestion.text}</span>
                          </button>
                        ))}
                        
                        {/* Keep original topic */}
                        <button
                          type="button"
                          onClick={() => handleSelectSuggestion(mainIdea.trim())}
                          className={cn(
                            "w-full p-3 text-left rounded-lg border-2 border-dashed transition-all duration-200",
                            "flex items-center gap-3 group text-sm",
                            selectedSuggestion === mainIdea.trim()
                              ? "border-primary bg-primary/10"
                              : "border-muted-foreground/30 bg-muted/20 hover:border-primary/50"
                          )}
                        >
                          <div className={cn(
                            "w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors",
                            selectedSuggestion === mainIdea.trim()
                              ? "border-primary bg-primary"
                              : "border-muted-foreground/30 group-hover:border-primary"
                          )}>
                            {selectedSuggestion === mainIdea.trim() ? (
                              <Check className="w-3 h-3 text-primary-foreground" />
                            ) : (
                              <Check className="w-3 h-3 text-muted-foreground group-hover:text-primary" />
                            )}
                          </div>
                          <span className="text-muted-foreground group-hover:text-foreground">
                            Zostań przy moim pomyśle
                          </span>
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Section 3: Mode Selection - Shows after topic selected */}
                {flowStep === 'mode_selection' && selectedSuggestion && (
                  <div className="space-y-4 pt-2 border-t border-border/30 animate-in fade-in-0 slide-in-from-bottom-2 duration-300">
                    <div className="flex items-center justify-between">
                      <h2 className="text-base font-medium text-foreground">
                        Sposób tworzenia
                      </h2>
                      {!generationMode && (
                        <span className="inline-flex items-center gap-1.5 text-xs text-primary animate-in fade-in-0 duration-500">
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                          </span>
                          Wybierz tryb
                        </span>
                      )}
                    </div>

                    {/* Mode Cards - Side by side */}
                    <div className={cn(
                      "grid grid-cols-2 gap-3",
                      !generationMode && "animate-in fade-in-0 slide-in-from-bottom-2 duration-500"
                    )}>
                      {MODE_OPTIONS.map((mode) => (
                        <button
                          key={mode.id}
                          type="button"
                          onClick={() => {
                            setGenerationMode(mode.id);
                            setVideoLength(null);
                            setShowVideoLengthHint(false);
                          }}
                          disabled={isGenerating}
                          className={cn(
                            "relative p-3 rounded-xl border-2 transition-all duration-200 text-left",
                            "flex items-center gap-3",
                            generationMode === mode.id
                              ? "border-primary bg-primary/5 scale-[1.02]"
                              : "border-border/50 hover:border-primary/50 hover:scale-[1.01]",
                            mode.isDefault && !generationMode && "border-primary/30 bg-primary/5"
                          )}
                        >
                          {/* Emoji */}
                          <span className="text-xl flex-shrink-0">{mode.emoji}</span>
                          
                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-sm text-foreground">{mode.label}</h3>
                            <p className="text-xs text-muted-foreground">{mode.description}</p>
                          </div>
                          
                          {/* Selection indicator */}
                          <div className={cn(
                            "w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors",
                            generationMode === mode.id
                              ? "border-primary bg-primary"
                              : "border-muted-foreground/30"
                          )}>
                            {generationMode === mode.id && (
                              <Check className="w-3 h-3 text-primary-foreground" />
                            )}
                          </div>
                        </button>
                      ))}
                    </div>

                    {/* Dopasowany Options */}
                    {generationMode === 'dostosowany' && (
                      <div className="space-y-4 pt-2 animate-in fade-in-0 duration-200">
                        
                        {/* Tone Selector */}
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-foreground">
                            Jak ma brzmieć?
                          </Label>
                          <div className="grid grid-cols-2 gap-2">
                            {TONE_OPTIONS.map((option, index) => (
                              <button
                                key={option.value}
                                type="button"
                                onClick={() => setToneIndex(index)}
                                disabled={isGenerating}
                                className={cn(
                                  "flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border-2 transition-all duration-200",
                                  "text-sm",
                                  toneIndex === index
                                    ? "border-primary bg-primary/10 text-foreground font-medium"
                                    : "border-border/50 text-muted-foreground hover:border-primary/50"
                                )}
                              >
                                <span>{option.icon}</span>
                                <span>{option.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                        
                        {/* Target Audience */}
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-foreground">
                            Dla kogo tworzysz?
                          </Label>
                          <div className="grid grid-cols-3 gap-2">
                            {GENDER_OPTIONS.map((option, index) => (
                              <button
                                key={option.value}
                                type="button"
                                onClick={() => setGenderIndex(index)}
                                disabled={isGenerating}
                                className={cn(
                                  "flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border-2 transition-all duration-200",
                                  "text-sm",
                                  genderIndex === index
                                    ? "border-primary bg-primary/10 text-foreground font-medium"
                                    : "border-border/50 text-muted-foreground hover:border-primary/50"
                                )}
                              >
                                <span>{option.icon}</span>
                                <span>{option.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                        {/* Video Length Selector */}
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-foreground">
                            Długość wideo
                          </Label>
                          <div className="grid grid-cols-2 gap-2">
                            {VIDEO_LENGTH_OPTIONS.map((option) => (
                              <button
                                key={option.value}
                                type="button"
                                onClick={() => {
                                  setVideoLength(option.value);
                                  setShowVideoLengthHint(false);
                                }}
                                disabled={isGenerating}
                                className={cn(
                                  "flex flex-col items-center justify-center gap-0.5 px-3 py-2.5 rounded-lg border-2 transition-all duration-200",
                                  "text-sm",
                                  videoLength === option.value
                                    ? "border-primary bg-primary/10 text-foreground font-medium"
                                    : showVideoLengthHint
                                      ? "border-destructive/50 text-muted-foreground hover:border-destructive"
                                      : "border-border/50 text-muted-foreground hover:border-primary/50"
                                )}
                              >
                                <div className="flex items-center gap-2">
                                  <span>{option.icon}</span>
                                  <span>{option.label}</span>
                                </div>
                                <span className="text-xs text-muted-foreground">{option.subtitle}</span>
                              </button>
                            ))}
                          </div>
                          {showVideoLengthHint && (
                            <p className="text-sm text-destructive" role="alert">Wybierz długość, aby kontynuować</p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Generate Button */}
                    {generationMode && !isGenerating && (
                      <Button
                        onClick={() => {
                          if (generationMode === 'dostosowany' && !videoLength) {
                            setShowVideoLengthHint(true);
                            return;
                          }
                          handleGenerate();
                        }}
                        disabled={generationMode === 'dostosowany' && !videoLength}
                        className="w-full h-12 text-base font-medium mt-2"
                        size="lg"
                      >
                        <span className="mr-2">🎬</span>
                        Wygeneruj scenariusz
                      </Button>
                    )}
                    
                    {/* Generation Loading State - at the bottom */}
                    {isGenerating && <GeneratingScenarioState progress={progress} timeLabel={generationTimeLabel} />}
                  </div>
                )}
              </div>
            </Card>

            {/* Generated Content Display */}
            {generatedContent && (
              <div className="animate-in slide-in-from-bottom-4 duration-500 space-y-3">
                <div className="flex items-center justify-between px-1">
                  <div className="flex items-center gap-2 text-primary">
                    <Sparkles className="h-5 w-5" />
                    <h3 className="text-base md:text-lg font-semibold">Twój ostatni przygotowany scenariusz:</h3>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearGeneratedContent}
                    className="text-muted-foreground hover:text-foreground h-8"
                  >
                    <X className="h-4 w-4 mr-1" />
                    <span className="hidden sm:inline">Zamknij</span>
                  </Button>
                </div>
                
                <ContentCard
                  idea={{
                    id: generatedContent.id,
                    user_id: '',
                    title: generatedContent.title,
                    description: generatedContent.description,
                    content: extractScript(generatedContent.content),
                    alt_hooks: normalizeArrayField(generatedContent.alt_hooks),
                    text_overlays: normalizeArrayField(generatedContent.text_overlays),
                    improvements_applied: normalizeArrayField(generatedContent.improvements_applied),
                    perplexity_insights: generatedContent.perplexity_insights || null,
                    platform: generatedContent.platform,
                    viral_score: generatedContent.viral_score,
                    hashtags: generatedContent.hashtags,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                    is_favorited: generatedContent.is_favorited || false,
                    is_used: generatedContent.is_used || false,
                    source: 'user_generated',
                    source_competitor: null,
                    main_idea: generatedContent.main_idea
                  } as ContentIdea}
                  isRowLayout={true}
                  isExpanded={true}
                  onToggleExpand={() => {}}
                  onCopyScript={(text) => {
                    navigator.clipboard.writeText(text);
                    toast({
                      title: "Skopiowano!",
                      description: "Tekst został skopiowany do schowka",
                    });
                  }}
                  onFavoriteToggle={async () => {
                    // Refetch the updated content after favorite toggle
                    const { data } = await supabase
                      .from('user_generated_content' as any)
                      .select('*')
                      .eq('id', generatedContent.id)
                      .single();
                    
                    if (data) {
                      setGeneratedContent(data as unknown as GeneratedContent);
                    }
                  }}
                />
              </div>
            )}
          </div>
        </main>
  );
};

export default IdeaGenerator;
